import { motion } from 'framer-motion';
import { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { AppSidebar } from '@/components/app/AppSidebar';
import { AppTopBar } from '@/components/app/AppTopBar';
import { MobileBottomNav } from '@/components/app/MobileBottomNav';
import { TrialBanner } from '@/components/app/TrialBanner';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import { useAuth } from '@/providers/AuthProvider';
import { env } from '@/lib/env';

export function AppLayout() {
  const { user, isLoading } = useAuth();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useRealtimeNotifications(user?.authUserId ?? user?.id);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-900">
        <div className="flex flex-col items-center gap-4">
          <div
            className="h-9 w-9 animate-spin rounded-full border-2 border-white/[0.08]"
            style={{ borderTopColor: 'var(--wl-primary-color, #6366f1)' }}
          />
          <p className="font-mono text-xs text-white/35 tracking-widest uppercase">Synchronising workspace…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return env.isE2E ? (
      <div className="min-h-screen bg-surface-900 text-white">
        <div className="p-6 text-sm text-white/70">E2E mode: user mock not yet ready.</div>
      </div>
    ) : (
      <Navigate to="/" replace />
    );
  }

  return (
    <div
      className="min-h-screen bg-surface-900 text-white"
      style={{
        backgroundImage:
          'radial-gradient(ellipse 100% 80% at 0% 0%, var(--ui-page-radial-1) 0%, transparent 55%), radial-gradient(ellipse 80% 70% at 100% 100%, var(--ui-page-radial-2) 0%, transparent 55%)',
      }}
    >
      {/* Subtle dot-grid texture */}
      <div
        className="dot-grid pointer-events-none fixed inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative mx-auto flex w-full max-w-[1440px] gap-5 px-4 py-4 lg:px-6 lg:py-5"
        style={{ paddingLeft: 'max(1rem, calc(1rem + env(safe-area-inset-left, 0px)))', paddingRight: 'max(1rem, calc(1rem + env(safe-area-inset-right, 0px)))' }}
      >
        <AppSidebar mobileOpen={mobileSidebarOpen} onMobileClose={() => setMobileSidebarOpen(false)} />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
          {/* Offline banner */}
          {env.isE2E && typeof navigator !== 'undefined' && !navigator.onLine ? (
            <div
              className="flex items-center gap-3 rounded-2xl border border-amber-400/25 bg-amber-500/8 px-4 py-3 text-sm text-amber-200"
              data-testid="offline-message"
            >
              <span className="h-2 w-2 flex-none rounded-full bg-amber-400" />
              You're offline — some data may be unavailable.
            </div>
          ) : null}

          <TrialBanner />
          <AppTopBar onMenuToggle={() => setMobileSidebarOpen(v => !v)} />

          {/* Main content — extra bottom padding on mobile for bottom nav */}
          <motion.main
            className="min-h-0 flex-1 pb-nav lg:pb-10"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut', delay: 0.05 }}
          >
            <Outlet />
          </motion.main>
        </div>
      </div>

      {/* Mobile bottom navigation */}
      <MobileBottomNav />
    </div>
  );
}
